/**
 * CompanyMembersPage — owner manages company members.
 * Mirrors Flutter `company/members_page.dart`. Phase-2 of the company
 * feature; in Phase 1 the owner is auto-seeded as the only member.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Users, UserPlus, Loader, Trash2, ShieldCheck } from 'lucide-react';
import { useCompanyMembers, useInviteMember } from '@/hooks/useCompany';
import { companiesApi } from '@/api/companies';
import { useQueryClient } from '@tanstack/react-query';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import { TextField } from '@/components/common/TextField';
import type { CompanyMember } from '@/types/company';

const ROLES: Array<{ key: CompanyMember['role']; label: string; hint: string }> = [
  { key: 'dispatcher', label: 'مرسِل عمليات', hint: 'يدير الشحنات والرحلات' },
  { key: 'driver',     label: 'سائق',         hint: 'ينفّذ رحلات باسم الشركة' },
  { key: 'accountant', label: 'محاسب',        hint: 'يطّلع على الأرباح والتسويات' },
  { key: 'viewer',     label: 'مُطّلع',       hint: 'قراءة فقط' },
];

const ROLE_LABEL: Record<CompanyMember['role'], string> = {
  owner: 'المالك',
  dispatcher: 'مرسِل عمليات',
  driver: 'سائق',
  accountant: 'محاسب',
  viewer: 'مُطّلع',
};

export default function CompanyMembersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: members, isLoading } = useCompanyMembers();
  const invite = useInviteMember();

  const [showInvite, setShowInvite] = useState(false);
  const [phone, setPhone] = useState('+963');
  const [role, setRole] = useState<CompanyMember['role']>('dispatcher');
  const [error, setError] = useState<string | null>(null);

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const cleaned = phone.replace(/\s+/g, '');
    if (!/^\+?\d{8,15}$/.test(cleaned)) {
      setError(t('phone_invalid', 'رقم الهاتف غير صحيح'));
      return;
    }
    try {
      await invite.mutateAsync({ phone: cleaned, role });
      setShowInvite(false);
      setPhone('+963');
      setRole('dispatcher');
    } catch (e: any) {
      setError(e?.message || t('invite_failed', 'فشل الدعوة'));
    }
  }

  async function handleRemove(memberId: number) {
    if (!confirm(t('confirm_remove_member', 'إزالة هذا العضو من الشركة؟'))) return;
    try {
      await companiesApi.removeMember(memberId);
      void qc.invalidateQueries({ queryKey: ['company', 'members'] });
    } catch (e: any) {
      alert(e?.message || 'فشل الإزالة');
    }
  }

  return (
    <AppLayout>
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary">
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
          <Users size={20} /> {t('company_members', 'أعضاء الشركة')}
        </h1>
        {!showInvite && (
          <Button onClick={() => setShowInvite(true)}>
            <UserPlus size={16} /> {t('invite_member', 'دعوة عضو')}
          </Button>
        )}
      </div>

      {/* Invite form */}
      {showInvite && (
        <div className="bg-surface border border-border rounded-2xl p-5 mb-4">
          <h2 className="font-bold text-text-primary mb-3">{t('invite_new_member', 'دعوة عضو جديد')}</h2>
          {error && <div className="bg-error/10 text-error rounded-xl p-3 text-sm mb-3">{error}</div>}
          <form onSubmit={handleInvite} className="space-y-3">
            <TextField
              label={t('phone_number', 'رقم الهاتف')}
              type="tel"
              dir="ltr"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+963 9XX XXX XXX"
            />
            <div>
              <div className="text-sm font-medium text-text-secondary mb-2">{t('role', 'الدور')}</div>
              <div className="grid grid-cols-2 gap-2">
                {ROLES.map((r) => (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => setRole(r.key)}
                    className={`text-start p-3 rounded-xl border-2 transition ${
                      role === r.key ? 'border-primary bg-primary/5' : 'border-border bg-surface hover:border-primary/50'
                    }`}
                  >
                    <div className="font-semibold text-sm text-text-primary">{r.label}</div>
                    <div className="text-xs text-text-tertiary mt-0.5">{r.hint}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" loading={invite.isPending}>
                <UserPlus size={16} /> {t('send_invite', 'إرسال الدعوة')}
              </Button>
              <Button variant="ghost" onClick={() => { setShowInvite(false); setError(null); }}>
                {t('cancel', 'إلغاء')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Members list */}
      {isLoading ? (
        <div className="flex justify-center py-12 text-text-tertiary">
          <Loader size={28} className="animate-spin" />
        </div>
      ) : !members || members.length === 0 ? (
        <div className="bg-surface border border-border rounded-2xl p-10 text-center">
          <Users size={40} className="mx-auto text-text-tertiary mb-3" />
          <p className="text-text-secondary">{t('no_members', 'لا أعضاء بعد — ادعُ موظفاً')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {members.map((m) => (
            <div key={m.id} className="bg-surface border border-border rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  {m.role === 'owner' ? <ShieldCheck size={18} /> : <Users size={18} />}
                </div>
                <div>
                  <div className="font-semibold text-text-primary">{m.user_name || t('unnamed_user', 'مستخدم')}</div>
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    {m.user_phone && <span dir="ltr" className="font-mono">{m.user_phone}</span>}
                    <span>· {ROLE_LABEL[m.role]}</span>
                  </div>
                </div>
              </div>
              {m.role !== 'owner' && (
                <button
                  onClick={() => handleRemove(m.id)}
                  className="p-2 rounded-lg hover:bg-error/10 text-error"
                  title={t('remove_member', 'إزالة')}
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
